import React, { useState, useRef, useEffect, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { uploadCertificate } from '../api/Classes';
import { getAllProfessions, Profession } from '../api/Professions';
import { useAuth } from '../useAuth';
import api from '../utils/api';

export interface ClassFormData {
  classname: string;
  classdate: string;
  classLU: number;
  took: boolean;
  classlink: string;
  meetlink: string;
  memo: string;
  islecture: boolean;
  isselfstudy: boolean;
  isRegistered: boolean;
  docpath: string;
  classwhen: string;
  classwhere: string;
  providerno: string;
  programno: string;
  cost?: number;
}

interface ClassFormProps {
  mode: 'create' | 'edit';
  initialData?: Partial<ClassFormData>;
  classId?: string; // For edit mode and certificate upload
  onSubmit: (data: ClassFormData) => Promise<void>;
  onCancel: () => void;
  submitting?: boolean;
  showCategoryPanel?: boolean;
  categoryPanel?: ReactNode; // Category panel content passed from parent
  title?: string;
}

const ClassForm: React.FC<ClassFormProps> = ({
  mode,
  initialData = {},
  classId,
  onSubmit,
  onCancel,
  submitting = false,
  showCategoryPanel = false,
  categoryPanel,
  title,
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [inputDate] = useState(new Date().toISOString().split('T')[0]); // Today's date

  // Form state
  const [formData, setFormData] = useState<ClassFormData>({
    classname: initialData.classname || '',
    classdate: initialData.classdate || '',
    classLU: initialData.classLU || 1,
    took: initialData.took || false,
    classlink: initialData.classlink || '',
    meetlink: initialData.meetlink || '',
    memo: initialData.memo || '',
    islecture: initialData.islecture !== undefined ? initialData.islecture : true,
    isselfstudy: initialData.isselfstudy !== undefined ? initialData.isselfstudy : false,
    isRegistered: initialData.isRegistered !== undefined ? initialData.isRegistered : true,
    docpath: initialData.docpath || '',
    classwhen: initialData.classwhen || '',
    classwhere: initialData.classwhere || '',
    providerno: initialData.providerno || '',
    programno: initialData.programno || '',
    cost: initialData.cost || undefined,
  });

  const isFree = (user?.subscription?.plan ?? 'free') === 'free';

  const [uploadError, setUploadError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [userProfession, setUserProfession] = useState<Profession | null>(null);
  const [creditTerm, setCreditTerm] = useState('CEC'); // Default credit terminology

  // Fetch user's profession to get credit terminology
  useEffect(() => {
    const fetchProfession = async () => {
      if (user?.professionID) {
        try {
          const professions = await getAllProfessions();
          const profession = professions.find(p => p.professionID === user.professionID);
          if (profession) {
            setUserProfession(profession);
            setCreditTerm(profession.creditTerminology);
          }
        } catch (err) {
          console.error('Error fetching profession:', err);
        }
      }
    };
    fetchProfession();
  }, [user?.professionID]);

  // Update form data when initialData changes (for edit mode)
  useEffect(() => {
    if (mode === 'edit' && initialData) {
      setFormData({
        classname: initialData.classname || '',
        classdate: initialData.classdate || '',
        classLU: initialData.classLU || 1,
        took: initialData.took || false,
        classlink: initialData.classlink || '',
        meetlink: initialData.meetlink || '',
        memo: initialData.memo || '',
        islecture: initialData.islecture !== undefined ? initialData.islecture : true,
        isselfstudy: initialData.isselfstudy !== undefined ? initialData.isselfstudy : false,
        isRegistered: initialData.isRegistered !== undefined ? initialData.isRegistered : true,
        docpath: initialData.docpath || '',
        classwhen: initialData.classwhen || '',
        classwhere: initialData.classwhere || '',
        providerno: initialData.providerno || '',
        programno: initialData.programno || '',
      });
    }
  }, [mode, initialData]);

  // Handle lecture/self-study radio button toggle
  const handleStudyTypeChange = (type: 'lecture' | 'selfstudy') => {
    if (type === 'lecture') {
      setFormData(prev => ({ ...prev, islecture: true, isselfstudy: false }));
    } else {
      setFormData(prev => ({ 
        ...prev, 
        islecture: false, 
        isselfstudy: true,
        classdate: inputDate // Auto-fill with today's date
      }));
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : type === 'number' ? Number(value) : value,
    }));
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Only allow upload in edit mode when we have a classId
    if (mode === 'create' || !classId) {
      setUploadError('Please save the class first before uploading a certificate.');
      return;
    }

    setUploading(true);
    setUploadError('');

    try {
      const result = await uploadCertificate(classId, file);
      setFormData(prev => ({ ...prev, docpath: result.docpath }));
      alert('Certificate uploaded successfully!');
    } catch (err: any) {
      console.error('Certificate upload failed:', err);
      const msg = err.response?.data?.error || err.message || 'Upload failed';
      setUploadError(msg);
    } finally {
      setUploading(false);
    }
  };

  const handleViewCertificate = async () => {
    if (!classId || !formData.docpath) return;

    try {
      const res = await api.get(`/api/classes/${classId}/certificate`);
      if (res.data?.url) {
        // S3 mode: open presigned URL directly — window.open is not subject to CORS
        window.open(res.data.url, '_blank', 'noopener,noreferrer');
      } else {
        // Local storage mode: response is a binary blob
        const blobRes = await api.get(`/api/classes/${classId}/certificate`, { responseType: 'blob' });
        const blob = new Blob([blobRes.data], { type: blobRes.headers['content-type'] || 'application/pdf' });
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank', 'noopener,noreferrer');
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
      }
    } catch (err: any) {
      console.error('Failed to view certificate:', err);
      alert('Failed to open certificate. Please try again.');
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!formData.classname || !formData.classdate || formData.classLU <= 0) {
      alert('Please fill in all required fields correctly.');
      return;
    }

    const dateYear = new Date(formData.classdate).getUTCFullYear();
    if (dateYear < 1970 || dateYear > new Date().getFullYear() + 2) {
      alert(`Please check the class date — the year "${dateYear}" doesn't look right.`);
      return;
    }

    await onSubmit(formData);
  };

  const defaultTitle = mode === 'create' ? 'Add New Class' : 'Edit Class';

  return (
    <div className="card shadow-sm">
      <div className="card-header bg-primary text-white">
        <h5 className="mb-0">
          <i className="bi bi-journal-text me-2"></i>
          {title || defaultTitle}
        </h5>
      </div>
      <div className="card-body">
        <form onSubmit={handleSubmit}>
          <div className="row g-3">
            {/* Class Name */}
            <div className="col-md-6">
              <label htmlFor="classname" className="form-label">Class Name *</label>
              <input
                type="text"
                id="classname"
                name="classname"
                className="form-control"
                value={formData.classname}
                onChange={handleInputChange}
                required
              />
            </div>

            {/* Study Type Radio Buttons */}
            <div className="col-md-3">
              <label className="form-label">Study Type *</label>
              <div>
                <div className="form-check form-check-inline">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="studyType"
                    id="radioLecture"
                    checked={formData.islecture}
                    onChange={() => handleStudyTypeChange('lecture')}
                  />
                  <label className="form-check-label" htmlFor="radioLecture">
                    Lecture
                  </label>
                </div>
                <div className="form-check form-check-inline">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="studyType"
                    id="radioSelfStudy"
                    checked={formData.isselfstudy}
                    onChange={() => handleStudyTypeChange('selfstudy')}
                  />
                  <label className="form-check-label" htmlFor="radioSelfStudy">
                    Self-Study
                  </label>
                </div>
              </div>
              {formData.isselfstudy ? (
                <small className="text-muted">Date auto-filled to today</small>
              ) : (
                <small className="text-muted">Class date must be filled</small>
              )}
            </div>

            {/* Class Date */}
            <div className="col-md-3">
              <label htmlFor="classdate" className="form-label">Date *</label>
              <input
                type="date"
                id="classdate"
                name="classdate"
                className="form-control"
                value={formData.classdate}
                onChange={handleInputChange}
                min="1970-01-01"
                max={`${new Date().getFullYear() + 2}-12-31`}
                required
              />
            </div>

            {/* Credits (LU/PDH/etc) */}
            <div className="col-md-3">
              <label htmlFor="classLU" className="form-label">
                {creditTerm} *
              </label>
              <input
                type="number"
                id="classLU"
                name="classLU"
                className="form-control"
                value={formData.classLU}
                onChange={handleInputChange}
                min="0.1"
                step="0.1"
                required
              />
            </div>

            {/* Checkboxes: Registered, Completed */}
            <div className="col-md-3">
              <div className="form-check mt-4">
                <input
                  type="checkbox"
                  id="isRegistered"
                  name="isRegistered"
                  className="form-check-input"
                  checked={formData.isRegistered}
                  onChange={handleInputChange}
                />
                <label htmlFor="isRegistered" className="form-check-label">Registered</label>
              </div>
            </div>
            <div className="col-md-3">
              <div className="form-check mt-4">
                <input
                  type="checkbox"
                  id="took"
                  name="took"
                  className="form-check-input"
                  checked={formData.took}
                  onChange={handleInputChange}
                />
                <label htmlFor="took" className="form-check-label">Completed</label>
              </div>
            </div>

            {/* Class Link */}
            <div className="col-md-6">
              <label htmlFor="classlink" className="form-label">Class Link</label>
              <input
                type="url"
                id="classlink"
                name="classlink"
                className="form-control"
                value={formData.classlink}
                onChange={handleInputChange}
                placeholder="https://... (optional)"
              />
              {formData.classlink && formData.classlink.startsWith('http') && (
                <small>
                  <a href={formData.classlink} target="_blank" rel="noopener noreferrer">
                    Open link
                  </a>
                </small>
              )}
            </div>

            {/* Meeting Link */}
            <div className="col-md-6">
              <label htmlFor="meetlink" className="form-label">Meeting Link</label>
              <input
                type="url"
                id="meetlink"
                name="meetlink"
                className="form-control"
                value={formData.meetlink}
                onChange={handleInputChange}
                placeholder="https://... (optional)"
              />
              {formData.meetlink && formData.meetlink.startsWith('http') && (
                <small>
                  <a href={formData.meetlink} target="_blank" rel="noopener noreferrer">
                    Open link
                  </a>
                </small>
              )}
            </div>

            {/* Class When */}
            <div className="col-md-6">
              <label htmlFor="classwhen" className="form-label">Class When</label>
              <input
                type="text"
                id="classwhen"
                name="classwhen"
                className="form-control"
                value={formData.classwhen}
                onChange={handleInputChange}
                placeholder="e.g., 9:00 AM - 5:00 PM"
              />
            </div>

            {/* Class Where */}
            <div className="col-md-6">
              <label htmlFor="classwhere" className="form-label">Class Where</label>
              <input
                type="text"
                id="classwhere"
                name="classwhere"
                className="form-control"
                value={formData.classwhere}
                onChange={handleInputChange}
                placeholder="Location or platform"
              />
            </div>

            {/* Provider No. - STRING FIELD */}
            <div className="col-md-6">
              <label htmlFor="providerno" className="form-label">Provider No.</label>
              <input
                type="text"
                id="providerno"
                name="providerno"
                className="form-control"
                value={formData.providerno}
                onChange={handleInputChange}
                placeholder="e.g., ABC123"
              />
            </div>

            {/* Program No. - STRING FIELD */}
            <div className="col-md-6">
              <label htmlFor="programno" className="form-label">Program No.</label>
              <input
                type="text"
                id="programno"
                name="programno"
                className="form-control"
                value={formData.programno}
                onChange={handleInputChange}
                placeholder="e.g., XYZ789"
              />
            </div>

            {/* Cost - OPTIONAL NUMBER FIELD */}
            <div className="col-md-6">
              <label htmlFor="cost" className="form-label">Cost ($)</label>
              <input
                type="number"
                id="cost"
                name="cost"
                className="form-control"
                value={formData.cost || ''}
                onChange={handleInputChange}
                placeholder="e.g., 150.00"
                min="0"
                step="0.01"
              />
              <small className="text-muted">Optional: Class fee or registration cost</small>
            </div>

            {/* Certificate Upload - only in edit mode */}
            {mode === 'edit' && classId && (
              <div className="col-12">
                <label className="form-label fw-bold">CE Certificate</label>
                <div className="border rounded p-3 bg-light">
                  {isFree && (
                    <div className="d-flex align-items-start gap-2 mb-3 p-2 rounded" style={{ background: '#fff8e1', border: '1px solid #fdd835' }}>
                      <span style={{ fontSize: '1rem', lineHeight: 1.4 }}>🔒</span>
                      <div style={{ fontSize: '0.82rem', color: '#5a4000', lineHeight: 1.5 }}>
                        <strong>Certificate storage requires Solo or Pro.</strong><br />
                        Upload and store your CE certificates so you have proof of completion on file.{' '}
                        <a href="/pricing" style={{ color: '#b45309', fontWeight: 600 }}>Upgrade →</a>
                      </div>
                    </div>
                  )}
                  <div
                    className="d-flex flex-column flex-sm-row gap-2 align-items-start align-items-sm-center"
                    style={isFree ? { opacity: 0.4, pointerEvents: 'none' } : undefined}
                  >
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading || isFree}
                    >
                      {uploading ? '◷ Uploading...' : (formData.docpath ? '↻ Replace' : '▤ Upload Certificate')}
                    </button>
                    <input
                      type="file"
                      className="d-none"
                      ref={fileInputRef}
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={handleFileUpload}
                      disabled={uploading || isFree}
                    />
                    <small className="text-muted">
                      PDF, JPG, or PNG (max 5MB)
                    </small>
                  </div>
                  {uploading && (
                    <div className="mt-2">
                      <div className="progress" style={{ height: '4px' }}>
                        <div className="progress-bar progress-bar-striped progress-bar-animated" role="progressbar" style={{ width: '100%' }}></div>
                      </div>
                    </div>
                  )}
                  {uploadError && (
                    <div className="alert alert-danger alert-sm mt-2 mb-0 py-2 px-3">
                      <small>✕ {uploadError}</small>
                    </div>
                  )}
                  {formData.docpath && !uploading && !isFree && (
                    <div className="mt-3 p-2 bg-white border rounded">
                      <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-2">
                        <div className="d-flex align-items-center gap-2">
                          <span className="badge bg-success">✓ Uploaded</span>
                          <span className="text-muted small">
                            {formData.docpath.split(/[\\/]/).pop()}
                          </span>
                        </div>
                        <button
                          type="button"
                          className="btn btn-sm btn-primary"
                          onClick={handleViewCertificate}
                        >
                          👁 View/Download
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Memo */}
            <div className="col-12">
              <label htmlFor="memo" className="form-label">Memo</label>
              <textarea
                id="memo"
                name="memo"
                className="form-control"
                rows={3}
                value={formData.memo}
                onChange={handleInputChange}
                placeholder="Additional notes..."
              />
            </div>

            {/* Category Panel - only shown when showCategoryPanel is true */}
            {showCategoryPanel && categoryPanel && (
              <div className="col-12">
                {categoryPanel}
              </div>
            )}
          </div>
          
          <div className="d-flex gap-2 mt-4">
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? (mode === 'create' ? 'Adding...' : 'Saving...') : (mode === 'create' ? 'Add Class' : 'Save Changes')}
            </button>
            <button type="button" className="btn btn-outline-secondary" onClick={onCancel}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ClassForm;
